import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, SelectChangeEvent, Stack, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DialogPlanApi, IDialogPlan } from '../../api/DialogPlanApi';
import { DialogPlanLlmPrompt, DialogPlanLlmPromptApi, DialogPlanLlmPromptConversationListen, DialogPlanLlmPromptConversationListenSpeak, DialogPlanLlmPromptConversationListenWithoutCharacter, DialogPlanLlmPromptConversationReadSpeak, DialogPlanLlmPromptConversationReadWithBlankSpeak, DialogPlanLlmPromptConversationSpeakOnly, DialogPlanLlmPromptFactory, DialogPlanLlmPromptImage, DialogPlanLlmPromptImageSelectSpeak, DialogPlanLlmPromptIntroduceWord, DialogPlanLlmPromptListenSpeak, DialogPlanLlmPromptListenSpeakNoGrade, DialogPlanLlmPromptMatchGame, DialogPlanLlmPromptQuizQuestion, DialogPlanLlmPromptQuizQuestionAudio, DialogPlanLlmPromptQuizQuestionSpeak, DialogPlanLlmPromptQuizWord, DialogPlanLlmPromptSelectNextDialog, DialogPlanLlmPromptSpeakNoGrade, DialogPlanLlmPromptSpeakWithGrade, DialogPlanLlmPromptStartNextLesson, DialogPlanLlmPromptTypeSpeak, DialogPlanLlmPromptTypeSpeakV2, IDialogPlanLlmPromptNextDialog } from '../../api/DialogPlanLlmPromptApi';
import AppBarContext from '../../context/AppBarContext';
import { handleErrorMessage } from '../ErrorDialog';
import { DialogPlanLlmPromptConversationListenForm } from './DialogPlanLlmPromptConversationListenForm';
import { DialogPlanLlmPromptConversationListenSpeakForm } from './DialogPlanLlmPromptConversationListenSpeakForm';
import { DialogPlanLlmPromptConversationListenWithoutCharacterForm } from './DialogPlanLlmPromptConversationListenWithoutCharacterForm';
import { DialogPlanLlmPromptConversationReadSpeakForm } from './DialogPlanLlmPromptConversationReadSpeakForm';
import { DialogPlanLlmPromptConversationReadWithBlankSpeakForm } from './DialogPlanLlmPromptConversationReadWithBlankSpeakForm';
import { DialogPlanLlmPromptConversationSpeakOnlyForm } from './DialogPlanLlmPromptConversationSpeakOnlyForm';
import { DialogPlanLlmPromptImageSelectSpeakForm } from './DialogPlanLlmPromptImageSelectSpeakForm';
import { DialogPlanLlmPromptMatchGameForm } from './DialogPlanLlmPromptMatchGameForm';
import { DialogPlanLlmPromptQuizQuestionAudioForm } from './DialogPlanLlmPromptQuizQuestionAudioForm';
import { DialogPlanLlmPromptQuizQuestionForm } from './DialogPlanLlmPromptQuizQuestionForm';
import { DialogPlanLlmPromptQuizQuestionSpeakForm } from './DialogPlanLlmPromptQuizQuestionSpeakForm';
import { DialogPlanLlmPromptQuizWordForm } from './DialogPlanLlmPromptQuizWordForm';
import DialogPlanLlmPromptSelectNextDialogOptions from './DialogPlanLlmPromptSelectNextDialogOptions';
import { DialogPlanLlmPromptSpeakNoGradeForm } from './DialogPlanLlmPromptSpeakNoGradeForm';
import { DialogPlanLlmPromptSpeakWithGradeForm } from './DialogPlanLlmPromptSpeakWithGradeForm';
import { DialogPlanLlmPromptTypeSpeakForm } from './DialogPlanLlmPromptTypeSpeakForm';
import { DialogPlanLlmPromptTypeSpeakV2Form } from './DialogPlanLlmPromptTypeSpeakV2Form';

interface DialogPlanLlmPromptFormProps {
    dialogPlanLlmPromptId?: number;
    originalDialogPlanLlmPrompt?: DialogPlanLlmPrompt;
    onSubmit?: (updatedDialogPlanLlmPrompt: DialogPlanLlmPrompt, setIsSaving: React.Dispatch<React.SetStateAction<boolean>>) => void;
    onDelete?: (dialogPlanLlmPromptId: number) => void;

    handleLlmPromptChange?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt) => void;
    handleRegenerateDialogPlanLlmPrompt?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt, fieldName: string) => void;
    isRegenerating?: boolean;
}

export function dialogPlanLlmPromptClassNameContainsProp(className: string, prop: string) {
    if (className == null || className.length <= 0) return false;
    // don't need to include all fields, just getting DialogPlanLlmPrompt object
    const selectedDialogPlanLlmPrompt = DialogPlanLlmPromptFactory(
        className,
        -1,
        -1,
        -1,
        true,
        "ERROR-dialogPlanLlmPromptClassNameContainsProp",
        true);
    return prop in selectedDialogPlanLlmPrompt
}

export function getClassNameDescription(className: string) {
    if (className === "DialogPlanLlmPromptModel") return "Dialog Only";
    if (className === "DialogPlanLlmPromptImageModel") return "Dialog w/ Image";
    if (className === "DialogPlanLlmPromptIntroduceWordModel") return "Dialog w/ Character Listen";
    if (className === "DialogPlanLlmPromptListenSpeakModel") return "Dialog w/ Listen & Speak";
    if (className === "DialogPlanLlmPromptListenSpeakNoGradeModel") return "Dialog w/ Listen & Speak Anything, No Grade";
    if (className === "DialogPlanLlmPromptImageSelectSpeakModel") return "Dialog w/ Image Select & Speak";

    if (className === "DialogPlanLlmPromptQuizWordModel") return "Quiz: Character Recognition";
    if (className === "DialogPlanLlmPromptMatchGameModel") return "Quiz: Matching Game";
    if (className === "DialogPlanLlmPromptQuizQuestionModel") return "Quiz: Select One";
    if (className === "DialogPlanLlmPromptQuizQuestionAudioModel") return "Quiz: Select One Audio";
    if (className === "DialogPlanLlmPromptQuizQuestionSpeakModel") return "Quiz: Select One, then Speak";
    if (className === "DialogPlanLlmPromptTypeSpeakModel") return "Prompt, Type, Speak";
    if (className === "DialogPlanLlmPromptTypeSpeakV2Model") return "Prompt, Type, Speak V2.0";
    if (className === "DialogPlanLlmPromptSpeakNoGradeModel") return "Prompt, Speak Anything, No Grade";
    if (className === "DialogPlanLlmPromptSpeakWithGradeModel") return "Prompt, Speak With Grade";

    if (className === "DialogPlanLlmPromptConversationListenModel") return "Conversation: Listen";
    if (className === "DialogPlanLlmPromptConversationListenSpeakModel") return "Conversation: Listen then Speak";
    if (className === "DialogPlanLlmPromptConversationListenWithoutCharacterModel") return "Conversation: Listen without Character";
    if (className === "DialogPlanLlmPromptConversationReadSpeakModel") return "Conversation: Read then Speak";
    if (className === "DialogPlanLlmPromptConversationReadWithBlankSpeakModel") return "Conversation: Read w/ Blank then Speak";
    if (className === "DialogPlanLlmPromptConversationSpeakOnlyModel") return "Conversation: Speak Only";

    if (className === "DialogPlanLlmPromptSelectNextDialogModel") return "Select next dialog";
    if (className === "DialogPlanLlmPromptSummaryModel") return "Speaking Summary";
    if (className === "DialogPlanLlmPromptStartNextLessonModel") return "Start next lesson";

    throw new Error("Unknown class name : " + className);
}

const DialogPlanLlmPromptForm: React.FC<DialogPlanLlmPromptFormProps> = ({ dialogPlanLlmPromptId, originalDialogPlanLlmPrompt, onSubmit, onDelete, handleLlmPromptChange, handleRegenerateDialogPlanLlmPrompt, isRegenerating }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [dialogPlanLlmPrompt, setDialogPlanLlmPrompt] = React.useState<DialogPlanLlmPrompt | null>(null);

    const [isGenerated, setIsGenerated] = React.useState(false);

    // Initial values reset by useEffect below
    const [id, setId] = React.useState(-1);
    const [dialogPlanId, setDialogPlanId] = React.useState(-1);
    const [dialogPlanList, setDialogPlanList] = React.useState<IDialogPlan[]>([]);
    const [className, setClassName] = React.useState('');

    const [isTeacher, setIsTeacher] = React.useState(true);
    const [prompt, setPrompt] = React.useState('');
    const [isNoteToStudent, setIsNoteToStudent] = React.useState(Boolean);
    const [orderIndex, setOrderIndex] = React.useState(-1);

    const [imageSource, setImageSource] = React.useState("");

    const [newWord, setNewWord] = React.useState("");
    const [newWordInfoText, setNewWordInfoText] = React.useState("");
    const [yesButtonText, setYesButtonText] = React.useState("");
    const [noButtonText, setNoButtonText] = React.useState("");
    const [yesResponse, setYesResponse] = React.useState("");
    const [noResponse, setNoResponse] = React.useState("");
    const [nextDialogList, setNextDialogList] = React.useState<IDialogPlanLlmPromptNextDialog[]>([]);

    const [isSaving, setIsSaving] = React.useState(false); // Add saving state

    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const syncedOriginalDialogPlanLlmPromptJsonRef = React.useRef<string | null>(null);

    const setDialogPlanLlmPromptFields = React.useCallback((dialogPlanLlmPrompt: DialogPlanLlmPrompt) => {
        setId(dialogPlanLlmPrompt.id);
        setDialogPlanId(dialogPlanLlmPrompt.dialog_plan_id);
        setClassName(dialogPlanLlmPrompt.class_name);

        setIsTeacher(dialogPlanLlmPrompt.is_teacher);
        setPrompt(dialogPlanLlmPrompt.prompt);
        setIsNoteToStudent(dialogPlanLlmPrompt.is_note_to_student ? dialogPlanLlmPrompt.is_note_to_student : false);
        setOrderIndex(dialogPlanLlmPrompt.order_index);

        if ("image_src" in dialogPlanLlmPrompt) {
            setImageSource((dialogPlanLlmPrompt as DialogPlanLlmPromptImage).image_src);
        }
        if ("new_word" in dialogPlanLlmPrompt) {
            setNewWord((dialogPlanLlmPrompt as DialogPlanLlmPromptIntroduceWord).new_word);
        }
        if ("new_word_info_text" in dialogPlanLlmPrompt) {
            setNewWordInfoText((dialogPlanLlmPrompt as DialogPlanLlmPromptIntroduceWord).new_word_info_text);
        }
        if ("no_button_text" in dialogPlanLlmPrompt) {
            setNoButtonText((dialogPlanLlmPrompt as DialogPlanLlmPromptStartNextLesson).no_button_text)
        }
        if ("yes_button_text" in dialogPlanLlmPrompt) {
            setYesButtonText((dialogPlanLlmPrompt as DialogPlanLlmPromptStartNextLesson).yes_button_text)
        }
        if ("no_response" in dialogPlanLlmPrompt) {
            setNoResponse((dialogPlanLlmPrompt as DialogPlanLlmPromptListenSpeak).no_response)
        }
        if ("yes_response" in dialogPlanLlmPrompt) {
            setYesResponse((dialogPlanLlmPrompt as DialogPlanLlmPromptListenSpeakNoGrade).yes_response)
        }
        if ("next_dialog_list" in dialogPlanLlmPrompt) {
            setNextDialogList((dialogPlanLlmPrompt as DialogPlanLlmPromptSelectNextDialog).next_dialog_list || [])
        } else {
            setNextDialogList([])
        }
    }, [])

    React.useEffect(() => {
        if (originalDialogPlanLlmPrompt) {
            setIsGenerated(true)
        } else {
            setIsGenerated(false)
        }

        const fetchDialogList = async () => {
            try {
                const response = await DialogPlanApi.getAll();
                setDialogPlanList(response.data);
            } catch (err: any) {
                handleErrorMessage(err, setErrorMessage, history, location);
            }
        };
        fetchDialogList();
    }, [history, location, originalDialogPlanLlmPrompt, setErrorMessage]);
    React.useEffect(() => {
        let isCurrent = true;
        if (dialogPlanLlmPromptId && !originalDialogPlanLlmPrompt) {
            DialogPlanLlmPromptApi.getById(dialogPlanLlmPromptId)
                .then((response) => {
                    if (!isCurrent) {
                        return;
                    }
                    setDialogPlanLlmPrompt(response.data)
                    setDialogPlanLlmPromptFields(response.data);
                })
                .catch(err => {
                    if (!isCurrent) {
                        return;
                    }
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }
        return () => {
            isCurrent = false;
        };
    }, [dialogPlanLlmPromptId, history, location, originalDialogPlanLlmPrompt, setDialogPlanLlmPromptFields, setErrorMessage])
    React.useEffect(() => {
        if (originalDialogPlanLlmPrompt) {
            const originalDialogPlanLlmPromptJson = JSON.stringify(originalDialogPlanLlmPrompt);
            if (syncedOriginalDialogPlanLlmPromptJsonRef.current === originalDialogPlanLlmPromptJson) {
                return;
            }
            syncedOriginalDialogPlanLlmPromptJsonRef.current = originalDialogPlanLlmPromptJson;
            setDialogPlanLlmPrompt(originalDialogPlanLlmPrompt);
            setDialogPlanLlmPromptFields(originalDialogPlanLlmPrompt);
        }
        // Only resync from the incoming generated prompt. Including local dialogPlanLlmPrompt
        // here resets in-progress form edits back to the server value.
    }, [originalDialogPlanLlmPrompt, setDialogPlanLlmPromptFields])

    React.useEffect(() => {
        if (dialogPlanLlmPrompt && "next_dialog_list" in dialogPlanLlmPrompt) {
            (dialogPlanLlmPrompt as DialogPlanLlmPromptSelectNextDialog).next_dialog_list = nextDialogList;
        }
    }, [nextDialogList, dialogPlanLlmPrompt])

    React.useEffect(() => {
        if (className == "DialogPlanLlmPromptSelectNextDialogModel") {
            const nextDialogPrompt = new DialogPlanLlmPromptSelectNextDialog(
                id,
                dialogPlanId,
                orderIndex,
                isTeacher,
                prompt,
                isNoteToStudent,
                nextDialogList
            );
            setDialogPlanLlmPrompt(nextDialogPrompt);
            if (handleLlmPromptChange) handleLlmPromptChange(nextDialogPrompt);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [className, id, dialogPlanId, orderIndex, isTeacher, prompt, isNoteToStudent, nextDialogList])

    // not needed if dialogPrompt given, fields are hidden and not used.
    const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setId(Number(e.target.value));
        if (dialogPlanLlmPrompt) dialogPlanLlmPrompt.id = Number(e.target.value)
    };
    const handleDialogPlanIdChange = (e: SelectChangeEvent<number>) => {
        setDialogPlanId(Number(e.target.value));
        if (dialogPlanLlmPrompt) dialogPlanLlmPrompt.dialog_plan_id = Number(e.target.value)
    };
    const handleClassNameChange = (e: SelectChangeEvent<string>) => {
        setClassName(e.target.value);
        if (dialogPlanLlmPrompt) dialogPlanLlmPrompt.class_name = e.target.value
    };
    const handleOrderIndexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setOrderIndex(Number(e.target.value));
        if (dialogPlanLlmPrompt) dialogPlanLlmPrompt.order_index = Number(e.target.value)
    };

    // update dialog prompt
    const handleIsTeacherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsTeacher(e.target.value === 'true');
        if (dialogPlanLlmPrompt) dialogPlanLlmPrompt.is_teacher = e.target.value === 'true'
    };
    const handlePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPrompt(e.target.value);
        if (dialogPlanLlmPrompt) dialogPlanLlmPrompt.prompt = e.target.value
    };
    const handleIsNoteToStudentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsNoteToStudent(e.target.checked);
        if (dialogPlanLlmPrompt) dialogPlanLlmPrompt.is_note_to_student = e.target.checked
    };
    const handleImageSourceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setImageSource(e.target.value);
        if (dialogPlanLlmPrompt) (dialogPlanLlmPrompt as DialogPlanLlmPromptImage).image_src = e.target.value
    };
    const handleNewWordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newWord = e.target.value;
        setNewWord(newWord);
        if (dialogPlanLlmPrompt) (dialogPlanLlmPrompt as DialogPlanLlmPromptIntroduceWord).new_word = newWord
    };
    const handleNewWordInfoTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewWordInfoText(e.target.value);
        if (dialogPlanLlmPrompt) (dialogPlanLlmPrompt as DialogPlanLlmPromptIntroduceWord).new_word_info_text = e.target.value
    };

    const handleYesButtonTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setYesButtonText(e.target.value);
        if (dialogPlanLlmPrompt) (dialogPlanLlmPrompt as DialogPlanLlmPromptStartNextLesson).yes_button_text = e.target.value
    };
    const handleNoButtonTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNoButtonText(e.target.value);
        if (dialogPlanLlmPrompt) (dialogPlanLlmPrompt as DialogPlanLlmPromptStartNextLesson).no_button_text = e.target.value
    };

    const handleYesResponseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setYesResponse(e.target.value);
        if (dialogPlanLlmPrompt) (dialogPlanLlmPrompt as DialogPlanLlmPromptListenSpeakNoGrade).yes_response = e.target.value
    };
    const handleNoResponseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNoResponse(e.target.value);
        if (dialogPlanLlmPrompt) (dialogPlanLlmPrompt as DialogPlanLlmPromptListenSpeak).no_response = e.target.value
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSaving(true); // Set saving state to true

        if (className == "DialogPlanLlmPromptQuizWordModel" ||
            className == "DialogPlanLlmPromptImageSelectSpeakModel" ||
            className == "DialogPlanLlmPromptMatchGameModel" ||
            className == "DialogPlanLlmPromptQuizQuestionModel" ||
            className == "DialogPlanLlmPromptQuizQuestionAudioModel" ||
            className == "DialogPlanLlmPromptQuizQuestionSpeakModel" ||
            className == "DialogPlanLlmPromptTypeSpeakModel" ||
            className == "DialogPlanLlmPromptTypeSpeakV2Model" ||
            className == "DialogPlanLlmPromptSpeakNoGradeModel" ||
            className == "DialogPlanLlmPromptSpeakWithGradeModel" ||
            className == "DialogPlanLlmPromptConversationListenModel" ||
            className == "DialogPlanLlmPromptConversationListenWithoutCharacterModel" ||
            className == "DialogPlanLlmPromptConversationListenSpeakModel" ||
            className == "DialogPlanLlmPromptConversationReadSpeakModel" ||
            className == "DialogPlanLlmPromptConversationSpeakOnlyModel" ||
            className == "DialogPlanLlmPromptConversationReadWithBlankSpeakModel" ||
            className == "DialogPlanLlmPromptSelectNextDialogModel") {
            if (dialogPlanLlmPrompt != null) {
                if (onSubmit) onSubmit(dialogPlanLlmPrompt, setIsSaving);
            }
        } else {
            const thisDialogPrompt = DialogPlanLlmPromptFactory(
                className,
                id,
                dialogPlanId,
                orderIndex,
                isTeacher,
                prompt,
                isNoteToStudent,
                imageSource as string | null,
                newWord as string | null,
                yesButtonText as string | null,
                noButtonText as string | null,
                yesResponse as string | null,
                noResponse as string | null,
                newWordInfoText as string | null
            )
            if (onSubmit) onSubmit(thisDialogPrompt, setIsSaving);
        }

    }

    const handleDeleteConfirmation = () => {
        setShowConfirmation(true);
    };

    const handleDeleteCancel = () => {
        setShowConfirmation(false);
    };

    const handleDeleteConfirm = () => {
        setShowConfirmation(false);
        if (onDelete && dialogPlanLlmPromptId) onDelete(dialogPlanLlmPromptId);
    };

    return (
        <form onSubmit={handleSubmit}>
            <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }}>
                <Divider sx={{ borderColor: '#FFBE19', borderWidth: '2px', mb: 2 }} />  {/* Customized Divider */}

                {dialogPlanLlmPromptId && <>
                    {/* hide these fields if generated prompt exists */}
                    <input type="hidden" value={id} onChange={handleIdChange} />
                    <input type="hidden" value={dialogPlanId} onChange={handleDialogPlanIdChange} />
                    <FormControl>
                        <InputLabel id="class-name-label">Class Name</InputLabel>
                        <Select
                            labelId="class-name-label"
                            id="class-name-select"
                            value={className}
                            onChange={handleClassNameChange}
                            required
                        >

                            <MenuItem value="DialogPlanLlmPromptModel">{getClassNameDescription("DialogPlanLlmPromptModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptImageModel">{getClassNameDescription("DialogPlanLlmPromptImageModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptIntroduceWordModel">{getClassNameDescription("DialogPlanLlmPromptIntroduceWordModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptListenSpeakModel">{getClassNameDescription("DialogPlanLlmPromptListenSpeakModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptListenSpeakNoGradeModel">{getClassNameDescription("DialogPlanLlmPromptListenSpeakNoGradeModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptImageSelectSpeakModel">{getClassNameDescription("DialogPlanLlmPromptImageSelectSpeakModel")}</MenuItem>

                            <MenuItem value="DialogPlanLlmPromptQuizWordModel">{getClassNameDescription("DialogPlanLlmPromptQuizWordModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptMatchGameModel">{getClassNameDescription("DialogPlanLlmPromptMatchGameModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptQuizQuestionModel">{getClassNameDescription("DialogPlanLlmPromptQuizQuestionModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptQuizQuestionAudioModel">{getClassNameDescription("DialogPlanLlmPromptQuizQuestionAudioModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptQuizQuestionSpeakModel">{getClassNameDescription("DialogPlanLlmPromptQuizQuestionSpeakModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptTypeSpeakModel">{getClassNameDescription("DialogPlanLlmPromptTypeSpeakModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptTypeSpeakV2Model">{getClassNameDescription("DialogPlanLlmPromptTypeSpeakV2Model")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptSpeakNoGradeModel">{getClassNameDescription("DialogPlanLlmPromptSpeakNoGradeModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptSpeakWithGradeModel">{getClassNameDescription("DialogPlanLlmPromptSpeakWithGradeModel")}</MenuItem>

                            <MenuItem value="DialogPlanLlmPromptConversationListenModel">{getClassNameDescription("DialogPlanLlmPromptConversationListenModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptConversationListenSpeakModel">{getClassNameDescription("DialogPlanLlmPromptConversationListenSpeakModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptConversationListenWithoutCharacterModel">{getClassNameDescription("DialogPlanLlmPromptConversationListenWithoutCharacterModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptConversationReadSpeakModel">{getClassNameDescription("DialogPlanLlmPromptConversationReadSpeakModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptConversationReadWithBlankSpeakModel">{getClassNameDescription("DialogPlanLlmPromptConversationReadWithBlankSpeakModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptConversationSpeakOnlyModel">{getClassNameDescription("DialogPlanLlmPromptConversationSpeakOnlyModel")}</MenuItem>

                            <MenuItem value="DialogPlanLlmPromptSelectNextDialogModel">{getClassNameDescription("DialogPlanLlmPromptSelectNextDialogModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptStartNextLessonModel">{getClassNameDescription("DialogPlanLlmPromptStartNextLessonModel")}</MenuItem>
                            <MenuItem value="DialogPlanLlmPromptSummaryModel">{getClassNameDescription("DialogPlanLlmPromptSummaryModel")}</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Parent Dialog Plan Name</InputLabel>
                        <Select
                            value={dialogPlanId}
                            label="Parent Dialog Name"
                            onChange={handleDialogPlanIdChange}
                            required
                        >
                            <MenuItem key={-1} value={-1} />
                            {dialogPlanList.map((dialogPlan) => (
                                <MenuItem key={dialogPlan.id} value={dialogPlan.id}>
                                    {dialogPlan.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </>}
                <FormControl component="fieldset">
                    <RadioGroup
                        row
                        aria-label="isTeacher"
                        name="isTeacher"
                        value={isTeacher.toString()}
                        onChange={handleIsTeacherChange}
                    >
                        <FormControlLabel value="true" control={<Radio required />} label="Teacher" />
                        <FormControlLabel value="false" control={<Radio required />} label="Student" />
                    </RadioGroup>
                </FormControl>
                <div>
                    <TextField
                        label="LLM Prompt"
                        value={prompt}
                        onChange={handlePromptChange}
                        multiline
                        rows={5}
                        sx={{ width: '100%', mt: 2 }}
                        inputProps={{
                            style: {
                                resize: 'vertical',
                                overflow: 'hidden',
                            },
                        }}
                    />
                    {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button variant="contained"
                                onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "prompt")}
                                disabled={isRegenerating}>
                                Regenerate
                                {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                            </Button>
                        </Box>
                    </>}
                    <FormControlLabel
                        control={<Checkbox checked={isNoteToStudent} onChange={handleIsNoteToStudentChange} />}
                        label="Is a Note to the Student"
                    />
                    {!isGenerated && <>
                        {/* hide these fields if generated prompt exists */}
                        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                            &lt;SkipLLM/&gt; will take the text verbatim and not ask the LLM to generate anything
                        </Typography>
                        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                            &lt;VocabularyWord1/&gt; will be replaced with Vocabulary Word Input when generating. <br />
                            Also &lt;VocabularyWord2/&gt;, &lt;VocabularyWord3/&gt;, ... , &lt;VocabularyWord10/&gt;
                        </Typography>
                        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                            &lt;CustomInput***/&gt; can be used for additional inputs
                        </Typography>
                        <Typography sx={{ color: 'grey.650', fontSize: '0.9rem' }}>
                            &lt;CustomInput***TextPinyinPair/&gt; can be used for word / pinyin pair of fields
                        </Typography>
                        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                            &lt;CustomInput***Image/&gt; can be used for single image input
                        </Typography>
                        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                            &lt;CustomInput***ImageList/&gt; can be used for multiple image input
                        </Typography>
                        {/* <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                            &lt;HskLevel/&gt; will be replaced with the number only, i.e. 1, 2, 3, 4, 5, 6
                        </Typography> */}
                        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                            &lt;StudentName/&gt; will be replaced with the student's name
                        </Typography>
                        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                            &lt;TeacherName/&gt; will be replaced with the teacher's name
                        </Typography>
                        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                            &lt;Microphone/&gt; will be replaced with the microphone icon for recording
                        </Typography>
                        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                            &lt;Speaker/&gt; will be replaced with the speaker icon for listening
                        </Typography>
                        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                            &lt;Info/&gt; will be replaced with the info icon for more details
                        </Typography>
                    </>}
                </div>

                {dialogPlanLlmPromptClassNameContainsProp(className, "image_src") && (<>
                    <TextField
                        label="LLM Prompt for Image"
                        value={imageSource}
                        onChange={handleImageSourceChange}
                        multiline
                        rows={5}
                        sx={{ width: '100%' }}
                        inputProps={{
                            style: {
                                resize: 'vertical',
                                overflow: 'hidden',
                            },
                        }}
                    />
                    {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button variant="contained"
                                onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "image_src")}
                                disabled={isRegenerating}>
                                Regenerate
                                {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                            </Button>
                        </Box>
                    </>}
                    {/* spacer for image on the right */}
                    <Box sx={{ height: '151px', marginTop: '0px !important' }} />
                </>)}

                {dialogPlanLlmPromptClassNameContainsProp(className, "new_word") && (<>
                    <TextField
                        label="New Word"
                        value={newWord}
                        onChange={handleNewWordChange}
                        required
                    />
                    <Box aria-hidden="true" sx={{ minHeight: '56px' }} />
                </>)}

                {dialogPlanLlmPromptClassNameContainsProp(className, "new_word_info_text") && (<>
                    <TextField
                        label="LLM Prompt for New Word Info Text"
                        value={newWordInfoText}
                        onChange={handleNewWordInfoTextChange}
                        multiline
                        rows={5}
                    />
                    {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button variant="contained"
                                onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "new_word_info_text")}
                                disabled={isRegenerating}>
                                Regenerate
                                {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                            </Button>
                        </Box>
                    </>}
                </>)}

                {dialogPlanLlmPromptClassNameContainsProp(className, "yes_button_text") && (<>
                    <TextField
                        label="LLM Prompt for Yes Button Text"
                        value={yesButtonText}
                        onChange={handleYesButtonTextChange}
                        multiline
                        rows={5}
                        required
                    />
                    {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button variant="contained"
                                onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "yes_button_text")}
                                disabled={isRegenerating}>
                                Regenerate
                                {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                            </Button>
                        </Box>
                    </>}
                </>)}
                {dialogPlanLlmPromptClassNameContainsProp(className, "no_button_text") && (<>
                    <TextField
                        label="LLM Prompt for No Button Text"
                        value={noButtonText}
                        onChange={handleNoButtonTextChange}
                        multiline
                        rows={5}
                        required
                    />
                    {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button variant="contained"
                                onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "no_button_text")}
                                disabled={isRegenerating}>
                                Regenerate
                                {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                            </Button>
                        </Box>
                    </>}
                </>)}

                {dialogPlanLlmPromptClassNameContainsProp(className, "yes_response") && (<>
                    <TextField
                        label="LLM Prompt for Yes Response"
                        value={yesResponse}
                        onChange={handleYesResponseChange}
                        multiline
                        rows={5}
                        required
                    />
                    {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button variant="contained"
                                onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "yes_response")}
                                disabled={isRegenerating}>
                                Regenerate
                                {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                            </Button>
                        </Box>
                    </>}
                </>)}
                {dialogPlanLlmPromptClassNameContainsProp(className, "no_response") && (<>
                    <TextField
                        label="LLM Prompt for No Response"
                        value={noResponse}
                        onChange={handleNoResponseChange}
                        multiline
                        rows={5}
                        required
                    />
                    {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button variant="contained"
                                onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "no_response")}
                                disabled={isRegenerating}>
                                Regenerate
                                {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                            </Button>
                        </Box>
                    </>}
                </>)}
                {className == "DialogPlanLlmPromptQuizWordModel" &&
                    <DialogPlanLlmPromptQuizWordForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptQuizWord)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} newWord={newWord}
                        yesResponse={yesResponse} noResponse={noResponse} />}
                {className == "DialogPlanLlmPromptImageSelectSpeakModel" &&
                    <DialogPlanLlmPromptImageSelectSpeakForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptImageSelectSpeak)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} yesResponse={yesResponse} noResponse={noResponse} />}
                {className == "DialogPlanLlmPromptMatchGameModel" &&
                    <DialogPlanLlmPromptMatchGameForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptMatchGame)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} yesResponse={yesResponse} noResponse={noResponse} />}
                {className == "DialogPlanLlmPromptQuizQuestionModel" &&
                    <DialogPlanLlmPromptQuizQuestionForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptQuizQuestion)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} yesResponse={yesResponse} noResponse={noResponse} imageSource={imageSource} />}
                {className == "DialogPlanLlmPromptQuizQuestionAudioModel" &&
                    <DialogPlanLlmPromptQuizQuestionAudioForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptQuizQuestionAudio)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} yesResponse={yesResponse} noResponse={noResponse} imageSource={imageSource} />}
                {className == "DialogPlanLlmPromptQuizQuestionSpeakModel" &&
                    <DialogPlanLlmPromptQuizQuestionSpeakForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptQuizQuestionSpeak)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} yesResponse={yesResponse} noResponse={noResponse} imageSource={imageSource} />}
                {className == "DialogPlanLlmPromptTypeSpeakModel" &&
                    <DialogPlanLlmPromptTypeSpeakForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptTypeSpeak)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} yesResponse={yesResponse} imageSource={imageSource} />}
                {className == "DialogPlanLlmPromptTypeSpeakV2Model" &&
                    <DialogPlanLlmPromptTypeSpeakV2Form dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptTypeSpeakV2)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} yesResponse={yesResponse} imageSource={imageSource} />}
                {className == "DialogPlanLlmPromptSpeakNoGradeModel" &&
                    <DialogPlanLlmPromptSpeakNoGradeForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptSpeakNoGrade)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} yesResponse={yesResponse} imageSource={imageSource} />}
                {className == "DialogPlanLlmPromptSpeakWithGradeModel" &&
                    <DialogPlanLlmPromptSpeakWithGradeForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptSpeakWithGrade)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} yesResponse={yesResponse} noResponse={noResponse} imageSource={imageSource} />}
                {className == "DialogPlanLlmPromptConversationListenModel" &&
                    <DialogPlanLlmPromptConversationListenForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptConversationListen)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} />}
                {className == "DialogPlanLlmPromptConversationListenWithoutCharacterModel" &&
                    <DialogPlanLlmPromptConversationListenWithoutCharacterForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptConversationListenWithoutCharacter)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} />}
                {className == "DialogPlanLlmPromptConversationListenSpeakModel" &&
                    <DialogPlanLlmPromptConversationListenSpeakForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptConversationListenSpeak)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} yesResponse={yesResponse} noResponse={noResponse} />}
                {className == "DialogPlanLlmPromptConversationReadSpeakModel" &&
                    <DialogPlanLlmPromptConversationReadSpeakForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptConversationReadSpeak)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} yesResponse={yesResponse} noResponse={noResponse} />}
                {className == "DialogPlanLlmPromptConversationSpeakOnlyModel" &&
                    <DialogPlanLlmPromptConversationSpeakOnlyForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptConversationSpeakOnly)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} yesResponse={yesResponse} noResponse={noResponse} />}
                {className == "DialogPlanLlmPromptConversationReadWithBlankSpeakModel" &&
                    <DialogPlanLlmPromptConversationReadWithBlankSpeakForm dialogPlanLlmPromptId={dialogPlanLlmPromptId}
                        dialogPlanLlmPrompt={dialogPlanLlmPromptId ? undefined : (dialogPlanLlmPrompt as DialogPlanLlmPromptConversationReadWithBlankSpeak)}
                        setDialogPlanLlmPrompt={setDialogPlanLlmPrompt} handleLlmPromptChange={handleLlmPromptChange}
                        handleRegenerateDialogPlanLlmPrompt={handleRegenerateDialogPlanLlmPrompt} isRegenerating={isRegenerating}
                        id={id} dialogPlanId={dialogPlanId} orderIndex={orderIndex} isTeacher={isTeacher} prompt={prompt}
                        isNoteToStudent={isNoteToStudent} yesResponse={yesResponse} noResponse={noResponse} />}
                {className == "DialogPlanLlmPromptSelectNextDialogModel" &&
                    <DialogPlanLlmPromptSelectNextDialogOptions
                        nextDialogList={nextDialogList}
                        setNextDialogList={setNextDialogList}
                        createNextDialog={() => ({
                            id: 0,
                            dialog_plan_llm_prompt_id: 0,
                            button_text: '',
                            response_text: '',
                            next_dialog_id: 0,
                            order_index: nextDialogList.length + 1,
                        })} />}
                {!isGenerated && <>
                    {/* hide these fields if generated prompt exists */}

                    <TextField
                        label="Sequence Number"
                        value={orderIndex}
                        onChange={handleOrderIndexChange}
                        type="number"
                        required
                    />
                    <br />
                    <Button disabled={isSaving} type="submit" variant="contained">
                        {isSaving ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirmation}>
                        Delete
                    </Button>
                </>}
            </Stack>
            <Dialog open={showConfirmation} onClose={handleDeleteCancel}>
                <DialogTitle>Delete DialogPlanLlmPrompt</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this dialogPlanLlmPrompt?</Typography>
                    <Typography>{prompt}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </form>
    );
};

export default DialogPlanLlmPromptForm;
